/**
 * AI Behavior Insights API Service
 *
 * All metric values come from real LLM calls (build_ai_behavior_prompt).
 * "How this is measured" text is fetched from the backend prompt explanation.
 * has_data=false → single empty state, no charts rendered.
 * Gemini excluded from all model maps.
 *
 * NOTE ON KEYS:
 *   - The backend returns CANONICAL keys ("openai", "claude", "perplexity", "llama").
 *   - The chart components consuming this module use DISPLAY keys
 *     ("gpt4", "claude", "perplexity", "llama") — a legacy convention left
 *     in place to avoid breaking existing chart configs.
 *   - `CANONICAL_TO_DISPLAY` bridges the two. Labels are pulled from the
 *     central catalog via the canonical id; only the chart-palette colors
 *     and the display-key indirection live here.
 */

import { apiFetch } from "../../../api/api-fetch";
import { getActiveBrand } from "./analytics-overview-api";
import { getModelLabel, type ProviderId } from "@/app/constants/models";

// ── Shared types ──────────────────────────────────────────────────────────────

export interface KPISummary {
  value: string;
  trend: string;
  trendDirection: "up" | "down" | "stable";
  label: string;
}

export interface Insight {
  label: string;
  value: string;
  description: string;
  type: "success" | "warning" | "info" | "action";
}

// ── Prompt explanation from backend ──────────────────────────────────────────

export interface PromptDimension {
  what_we_ask: string;
  how_scored: string;
  why_it_matters: string;
}

export interface PromptExplanation {
  reasoning_influence: PromptDimension;
  decision_stability: PromptDimension;
  schema_content_authority_narrative: PromptDimension;
}

// ── Reasoning Influence ───────────────────────────────────────────────────────

export interface ReasoningInfluenceTimePoint {
  date: string;
  ecosystemAvg: number;
  [modelKey: string]: number | string;
}

export interface ReasoningInfluenceResponse {
  kpiSummary: KPISummary;
  timeSeriesData: ReasoningInfluenceTimePoint[];
  annotations: { date: string; label: string; type: string }[];
  insights: Insight[];
  lastUpdated: string;
  promptExplanation: PromptDimension | null;
}

// ── Fix Categories ────────────────────────────────────────────────────────────

export interface FixCategoryDataPoint {
  snapshot: number;
  date: string;
  schema_score: number;
  content_score: number;
  authority_score: number;
  narrative_score: number;
}

export interface FixDecayResponse {
  kpiSummary: KPISummary;
  modelData: Record<string, FixCategoryDataPoint[]>;
  insights: Insight[];
  lastUpdated: string;
  promptExplanation: PromptDimension | null;
}

// ── Absorption Lag ────────────────────────────────────────────────────────────

export interface AbsorptionLagDataPoint {
  model: string;
  schema_score: number;
  content_score: number;
  authority_score: number;
  narrative_score: number;
  type: "Fast" | "Medium" | "Slow";
}

export interface AbsorptionLagResponse {
  kpiSummary: KPISummary;
  modelData: AbsorptionLagDataPoint[];
  insights: Insight[];
  lastUpdated: string;
}

// ── Decision Stability ────────────────────────────────────────────────────────

export interface DecisionStabilityDataPoint {
  date: string;
  stability: number;
}

export interface DecisionStabilityResponse {
  kpiSummary: KPISummary;
  timeSeriesData: Record<string, DecisionStabilityDataPoint[]>;
  insights: Insight[];
  lastUpdated: string;
  promptExplanation: PromptDimension | null;
}

// ── Summary ───────────────────────────────────────────────────────────────────

export interface AIBehaviorSummary {
  hasData: boolean;
  runCount: number;
  snapshotCount: number;
  avgReasoningInfluence: KPISummary;
  avgFixLongevity: KPISummary;
  avgStabilityScore: KPISummary;
  reasoningInfluenceInsights: Insight[];
  fixDecayInsights: Insight[];
  decisionStabilityInsights: Insight[];
  lastUpdated: string;
}

// ── Model config ──────────────────────────────────────────────────────────────
//
// Display keys ("gpt4", …) are the legacy chart-consumer convention. The
// backend uses canonical ProviderIds ("openai", …). Bridge maps below.

const CANONICAL_TO_DISPLAY: Record<string, string> = {
  openai:     "gpt4",
  claude:     "claude",
  perplexity: "perplexity",
  llama:      "llama",
};

// Reverse bridge so we can resolve a display key back to a catalog entry
// for label lookup.
const DISPLAY_TO_CANONICAL: Record<string, ProviderId> = {
  gpt4:       "openai",
  claude:     "claude",
  perplexity: "perplexity",
  llama:      "llama",
};

/** Display labels derived from the central catalog. */
export const MODEL_LABELS: Record<string, string> = Object.entries(DISPLAY_TO_CANONICAL).reduce(
  (acc, [displayKey, canonicalId]) => {
    acc[displayKey] = getModelLabel(canonicalId);
    return acc;
  },
  {} as Record<string, string>,
);

/**
 * Chart-specific color palette for AI Behavior charts. Intentionally local
 * (different from the brand-accent colors in models.ts) because this view
 * uses the chart-palette tokens to keep series legible together.
 */
export const MODEL_COLORS: Record<string, string> = {
  gpt4:       "var(--chart-2)",
  claude:     "var(--chart-1)",
  perplexity: "var(--chart-5)",
  llama:      "var(--chart-3)",
};

function toDisplayKey(canonical: string): string {
  return CANONICAL_TO_DISPLAY[canonical] ?? canonical;
}

function isExcluded(key: string): boolean {
  return key === "gemini";
}

// ── Transformers ──────────────────────────────────────────────────────────────

function toKPI(raw: any): KPISummary {
  return {
    value:          String(raw?.value          ?? "0"),
    trend:          String(raw?.trend          ?? "+0%"),
    trendDirection: (raw?.trend_direction as "up" | "down" | "stable") ?? "stable",
    label:          String(raw?.label          ?? ""),
  };
}

function toInsights(raw: any): Insight[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item: any) => ({
    label:       String(item?.label       ?? ""),
    value:       String(item?.value       ?? ""),
    description: String(item?.description ?? ""),
    type:        (item?.type as Insight["type"]) ?? "info",
  }));
}

function toPromptDimension(raw: any): PromptDimension | null {
  if (!raw) return null;
  return {
    what_we_ask:    String(raw.what_we_ask    ?? ""),
    how_scored:     String(raw.how_scored     ?? ""),
    why_it_matters: String(raw.why_it_matters ?? ""),
  };
}

// ── Cache ─────────────────────────────────────────────────────────────────────

interface CacheEntry { brand: string; data: any; ts: number }
let _cache: CacheEntry | null = null;
const CACHE_TTL_MS = 60_000;

export function triggerAIBehaviorRefresh(): void {
  _cache = null;
  try { window.dispatchEvent(new CustomEvent("ai-behavior-refresh")); } catch { /* SSR */ }
}

async function fetchRaw(): Promise<any> {
  const brand = getActiveBrand();
  if (!brand) throw new Error("NO_BRAND");
  const now = Date.now();
  if (_cache && _cache.brand === brand && now - _cache.ts < CACHE_TTL_MS) return _cache.data;
  const data = await apiFetch(`/analytics/ai-behavior?brand=${encodeURIComponent(brand)}&days=90`);
  _cache = { brand, data, ts: now };
  return data;
}

// ── Public fetch functions ────────────────────────────────────────────────────

export async function fetchAIBehaviorSummary(): Promise<AIBehaviorSummary> {
  const raw = await fetchRaw();
  const ri: any = raw?.reasoning_influence ?? {};
  const fd: any = raw?.fix_decay          ?? {};
  const ds: any = raw?.decision_stability ?? {};

  return {
    hasData:        Boolean(raw?.has_data),
    runCount:       Number(raw?.run_count      ?? 0),
    snapshotCount:  Number(raw?.snapshot_count ?? 0),
    avgReasoningInfluence: toKPI(ri.kpi_summary),
    avgFixLongevity:       toKPI(fd.kpi_summary),
    avgStabilityScore:     toKPI(ds.kpi_summary),
    reasoningInfluenceInsights: toInsights(ri.insights),
    fixDecayInsights:           toInsights(fd.insights),
    decisionStabilityInsights:  toInsights(ds.insights),
    lastUpdated: String(raw?.last_updated ?? ""),
  };
}

export async function fetchReasoningInfluenceData(): Promise<ReasoningInfluenceResponse> {
  const raw = await fetchRaw();
  const ri: any = raw?.reasoning_influence ?? {};
  const canonicalModels: string[] = (raw?.models ?? []).filter((m: string) => !isExcluded(toDisplayKey(m)));
  const promptExpl = raw?.prompt_explanation;

  const timeSeriesData: ReasoningInfluenceTimePoint[] = (ri.time_series ?? []).map((point: any) => {
    const remapped: Record<string, number | string> = {
      date: String(point.date ?? ""),
      ecosystemAvg: Number(point.ecosystemAvg ?? 0),
    };
    Object.values(CANONICAL_TO_DISPLAY).forEach(k => { remapped[k] = 0; });
    for (const canonical of canonicalModels) {
      const display = toDisplayKey(canonical);
      if (!isExcluded(display)) remapped[display] = Number(point[canonical] ?? 0);
    }
    return remapped as ReasoningInfluenceTimePoint;
  });

  return {
    kpiSummary:       toKPI(ri.kpi_summary),
    timeSeriesData,
    annotations:      Array.isArray(ri.annotations) ? ri.annotations : [],
    insights:         toInsights(ri.insights),
    lastUpdated:      String(raw?.last_updated ?? ""),
    promptExplanation: toPromptDimension(promptExpl?.reasoning_influence),
  };
}

export async function fetchFixDecayData(): Promise<FixDecayResponse> {
  const raw = await fetchRaw();
  const fd: any  = raw?.fix_decay ?? {};
  const promptExpl = raw?.prompt_explanation;

  const rawModelData: Record<string, any[]> = fd.model_data ?? {};
  const modelData: Record<string, FixCategoryDataPoint[]> = {};

  for (const [canonical, series] of Object.entries(rawModelData)) {
    const display = toDisplayKey(canonical);
    if (isExcluded(display)) continue;
    modelData[display] = (series as any[]).map((p: any): FixCategoryDataPoint => ({
      snapshot:        Number(p.snapshot        ?? 0),
      date:            String(p.date            ?? ""),
      schema_score:    Number(p.schema_score    ?? 0),
      content_score:   Number(p.content_score   ?? 0),
      authority_score: Number(p.authority_score ?? 0),
      narrative_score: Number(p.narrative_score ?? 0),
    }));
  }

  return {
    kpiSummary:       toKPI(fd.kpi_summary),
    modelData,
    insights:         toInsights(fd.insights),
    lastUpdated:      String(raw?.last_updated ?? ""),
    promptExplanation: toPromptDimension(promptExpl?.schema_content_authority_narrative),
  };
}

export async function fetchAbsorptionLagData(): Promise<AbsorptionLagResponse> {
  const raw = await fetchRaw();
  const al: any = raw?.absorption_lag ?? {};

  return {
    kpiSummary:  toKPI(al.kpi_summary),
    modelData:   (al.model_data ?? [])
      .filter((item: any) => !isExcluded(toDisplayKey(item?.model ?? "")))
      .map((item: any): AbsorptionLagDataPoint => ({
        model:           MODEL_LABELS[toDisplayKey(item.model ?? "")] ?? item.model ?? "",
        schema_score:    Number(item.schema_score    ?? 0),
        content_score:   Number(item.content_score   ?? 0),
        authority_score: Number(item.authority_score ?? 0),
        narrative_score: Number(item.narrative_score ?? 0),
        type:            (item.type as "Fast"|"Medium"|"Slow") ?? "Medium",
      })),
    insights:    toInsights(al.insights),
    lastUpdated: String(raw?.last_updated ?? ""),
  };
}

export async function fetchDecisionStabilityData(): Promise<DecisionStabilityResponse> {
  const raw = await fetchRaw();
  const ds: any = raw?.decision_stability ?? {};
  const promptExpl = raw?.prompt_explanation;

  const rawModelData: Record<string, any[]> = ds.model_data ?? {};
  const timeSeriesData: Record<string, DecisionStabilityDataPoint[]> = {};
  for (const [canonical, series] of Object.entries(rawModelData)) {
    const display = toDisplayKey(canonical);
    if (isExcluded(display)) continue;
    timeSeriesData[display] = (series as any[]).map((p: any) => ({
      date:      String(p.date      ?? ""),
      stability: Number(p.stability ?? 0),
    }));
  }

  return {
    kpiSummary:       toKPI(ds.kpi_summary),
    timeSeriesData,
    insights:         toInsights(ds.insights),
    lastUpdated:      String(ds.last_updated ?? raw?.last_updated ?? ""),
    promptExplanation: toPromptDimension(promptExpl?.decision_stability),
  };
}